/**
 * The "Control chart signal" condition of the alarm rule form (plant-analytics S15): one of the target
 * tag's active control charts (`GET /v1/twin/spc/charts?tag_id=`) and the chart rules that fire the alarm.
 * It fires only on the chart's new live signals, never on its history (`lib/alarms/spc-condition.ts`).
 */
import { isLiveApi } from "@/lib/api";
import { spcNotChartedText, useSpcChartsForTag, type SpcChartList } from "@/lib/api/spc";
import {
  SPC_ALARM_LIVE_ONLY,
  SPC_ALARM_RULES,
  spcAlarmChartLabel,
  spcAlarmCharts,
  spcAlarmPortChartNote,
  spcAlarmRulesFor,
  spcToggleAlarmRule,
} from "@/lib/alarms/spc-condition";
import { useSpcAlarmOfferedRules } from "@/lib/alarms/spc-offered";

export interface SpcAlarmConditionFieldsProps {
  tagId: string;
  chartId: string;
  rules: string[];
  onChange: (next: { chartId: string; rules: string[] }) => void;
}

/** The fields, reading the target tag's charts and the rules the rule's chart raises. */
export function SpcAlarmConditionFields(props: SpcAlarmConditionFieldsProps) {
  const list = useSpcChartsForTag(props.tagId || null);
  const offered = useSpcAlarmOfferedRules(props.tagId, props.chartId);
  return (
    <SpcAlarmConditionView
      {...props}
      live={isLiveApi}
      loading={list.isLoading}
      failed={!!list.error}
      list={list.data}
      offered={offered}
    />
  );
}

export interface SpcAlarmConditionViewProps extends SpcAlarmConditionFieldsProps {
  /** Charts come from the live analysis only. */
  live: boolean;
  loading: boolean;
  failed: boolean;
  /** The tag's active charts, and why it has none. */
  list: SpcChartList | undefined;
  /**
   * The rules the rule's chart raises, when known (a paused chart's are read off the chart); otherwise the picked
   * active chart's type's, or every rule.
   */
  offered?: readonly string[];
}

/** The fields over a given chart list. */
export function SpcAlarmConditionView({
  tagId,
  chartId,
  rules,
  onChange,
  live,
  loading,
  failed,
  list,
  offered: known,
}: SpcAlarmConditionViewProps) {
  const charts = spcAlarmCharts(list?.charts, tagId);
  const chart = charts.find((c) => c.id === chartId);
  // Only the rules the chart raises are on offer (#926 L3). A rule's chart that is no longer active (paused) is
  // still shown, with its type's rules once the chart is read, and every rule until then.
  const offered = known ?? spcAlarmRulesFor(chart?.chart_type);

  let note: string | null = null;
  if (!tagId) note = "Pick the target tag first: the alarm watches one of its control charts.";
  else if (!live) note = "Control charts come from the live analysis; the offline preview has none.";
  else if (loading) note = "Loading the tag's control charts...";
  else if (failed) note = "The tag's control charts could not be loaded.";
  else if (charts.length === 0 && !chartId) note = spcAlarmPortChartNote(list, tagId) ?? spcNotChartedText(list, tagId);

  // Picking a chart ticks every rule it raises; the user unticks what should not fire.
  const pick = (id: string) => {
    const next = charts.find((c) => c.id === id);
    onChange({ chartId: id, rules: id ? spcAlarmRulesFor(next?.chart_type) : [] });
  };
  const toggle = (rule: string, on: boolean) => onChange({ chartId, rules: spcToggleAlarmRule(rules, offered, rule, on) });

  return (
    <div className="flex flex-col gap-3" data-tour="alarm-rule-spc">
      {note ? (
        <p className="text-xs text-on-surface-muted">{note}</p>
      ) : (
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">Control chart</span>
          <select
            value={chartId}
            onChange={(e) => pick(e.target.value)}
            className="h-9 w-full rounded-md border border-border bg-surface px-2 text-sm text-on-surface"
          >
            <option value="">Choose a chart of this tag</option>
            {chartId && !chart && <option value={chartId}>The rule&apos;s chart (not active now)</option>}
            {charts.map((c) => (
              <option key={c.id} value={c.id}>
                {spcAlarmChartLabel(c)}
              </option>
            ))}
          </select>
        </label>
      )}
      {chartId && (
        <fieldset className="flex flex-col gap-1.5">
          <legend className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
            Rules that fire it
          </legend>
          {SPC_ALARM_RULES.filter((r) => offered.includes(r.rule)).map((r) => (
            <label key={r.rule} className="flex cursor-pointer items-center gap-2 text-xs text-on-surface">
              <input
                type="checkbox"
                className="size-3.5"
                checked={rules.includes(r.rule)}
                onChange={(e) => toggle(r.rule, e.target.checked)}
              />
              {r.label}
            </label>
          ))}
        </fieldset>
      )}
      <p className="text-[11px] text-on-surface-muted">{SPC_ALARM_LIVE_ONLY}</p>
    </div>
  );
}
